import React from 'react';
import { ChevronLeft, ChevronRight, Briefcase, Filter, Calendar as CalendarIcon, List } from 'lucide-react';
import { format, addMonths, subMonths, addWeeks, subWeeks, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ViewMode } from '../../types';


interface HeaderProps {
    currentDate: Date;
    setCurrentDate: (date: Date) => void;
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    // Filters
    services: string[];
    selectedService: string;
    setSelectedService: (service: string) => void;
    // View Options
    showWeekends: boolean;
    setShowWeekends: (show: boolean) => void;
    // Actions
    onNewAssignment?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
    currentDate,
    setCurrentDate,
    viewMode,
    setViewMode,
    services,
    selectedService,
    setSelectedService,
    showWeekends,
    setShowWeekends,
    onNewAssignment
}) => {

    const handlePrev = () => {
        if (viewMode === 'year') return; // Locked to 2026

        let newDate = currentDate;
        if (viewMode === 'week') {
            newDate = subWeeks(currentDate, 1);
        } else {
            newDate = subMonths(currentDate, 1);
        }

        if (newDate.getFullYear() === 2026) {
            setCurrentDate(newDate);
        }
    };

    const handleNext = () => {
        if (viewMode === 'year') return; // Locked to 2026

        let newDate = currentDate;
        if (viewMode === 'week') {
            newDate = addWeeks(currentDate, 1);
        } else {
            newDate = addMonths(currentDate, 1);
        }

        if (newDate.getFullYear() === 2026) {
            setCurrentDate(newDate);
        }
    };

    const formatDateDisplay = () => {
        if (viewMode === 'year') return 'Année 2026';
        if (viewMode === 'week') {
            const start = startOfWeek(currentDate, { weekStartsOn: 1 });
            const end = endOfWeek(currentDate, { weekStartsOn: 1 });
            // Si même mois
            if (start.getMonth() === end.getMonth()) {
                return `${format(start, 'd')} - ${format(end, 'd MMMM yyyy', { locale: fr })}`;
            }
            return `${format(start, 'd MMM')} - ${format(end, 'd MMM yyyy', { locale: fr })}`;
        }
        return format(currentDate, 'MMMM yyyy', { locale: fr }).charAt(0).toUpperCase() + format(currentDate, 'MMMM yyyy', { locale: fr }).slice(1);
    };

    return (
        <div className="flex flex-col gap-4 mb-4">
            {/* Top Bar with Title and Date Navigation */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 glass-panel rounded-2xl border border-glass-border shadow-lg">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-primary/10 rounded-xl">
                        <Briefcase className="text-primary w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-main leading-tight">
                            Planning Techniciens
                        </h1>
                        <p className="text-xs text-muted font-medium">Gestion d'équipe</p>
                    </div>
                </div>

                <div className="flex items-center gap-4 bg-input p-1.5 rounded-xl border border-glass-border shadow-inner">
                    <button
                        onClick={handlePrev}
                        disabled={viewMode === 'year'}
                        className="p-1.5 hover:bg-glass-border/10 rounded-lg text-muted hover:text-main transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <div className="min-w-[200px] text-center font-bold text-main capitalize">
                        {formatDateDisplay()}
                    </div>
                    <button
                        onClick={handleNext}
                        disabled={viewMode === 'year'}
                        className="p-1.5 hover:bg-glass-border/10 rounded-lg text-muted hover:text-main transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>


            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 p-3 glass-panel rounded-xl border border-glass-border/50">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-muted text-sm font-medium px-2">
                        <Filter size={16} />
                        <span>Filtres :</span>
                    </div>

                    <div className="relative group">
                        <select
                            value={selectedService}
                            onChange={(e) => setSelectedService(e.target.value)}
                            className="appearance-none bg-input border border-glass-border text-main text-sm rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer hover:bg-glass-border/10 transition-colors min-w-[180px]"
                        >
                            <option value="Tous">Tous les services</option>
                            {services.map(service => (
                                <option key={service} value={service}>{service}</option>
                            ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted">
                            <ChevronRight size={14} className="rotate-90" />
                        </div>
                    </div>

                    {/* View Mode Selector */}
                    <div className="flex bg-input p-1 rounded-lg border border-glass-border">
                        <button
                            onClick={() => setViewMode('week')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-2 ${viewMode === 'week'
                                ? 'bg-primary text-white shadow-md'
                                : 'text-muted hover:text-main hover:bg-glass-border/10'
                                }`}
                        >
                            <List size={14} />
                            Semaine
                        </button>
                        <button
                            onClick={() => setViewMode('month')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-2 ${viewMode === 'month'
                                ? 'bg-primary text-white shadow-md'
                                : 'text-muted hover:text-main hover:bg-glass-border/10'
                                }`}
                        >
                            <CalendarIcon size={14} />
                            Mois
                        </button>
                        <button
                            onClick={() => setViewMode('year')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-2 ${viewMode === 'year'
                                ? 'bg-primary text-white shadow-md'
                                : 'text-muted hover:text-main hover:bg-glass-border/10'
                                }`}
                        >
                            <CalendarIcon size={14} />
                            Année
                        </button>
                    </div>

                    {/* New Assignment Button (conditionally rendered) */}
                    {onNewAssignment && (
                        <button
                            onClick={onNewAssignment}
                            className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-lg shadow-primary/20"
                        >
                            <Briefcase size={16} />
                            Nouveau Chantier
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowWeekends(!showWeekends)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${showWeekends
                            ? 'bg-primary/20 text-primary border-primary/30'
                            : 'bg-transparent text-muted border-glass-border hover:border-glass-border/50'
                            }`}
                    >
                        {showWeekends ? 'Masquer WE' : 'Afficher WE'}
                    </button>
                </div>
            </div>
        </div>
    );
};
